import { AbstractControl } from "@angular/forms";

/**
 * A list of the possible GLN types.
 * @type {GLNType[]}
 */
export const glnTypes: GLNType[] = ['LEGAL_ENTITY', 'FIXED_PHYSICAL_LOCATION', 'FUNCTION'];

/**
 * A type for the GLN type.
 * @typedef {string} GLNType
 * @property {string} LEGAL_ENTITY - A legal entity GLN.
 * @property {string} FIXED_PHYSICAL_LOCATION - A fixed physical location GLN.
 * @property {string} MOBILE_PHYSICAL_LOCATION - A mobile physical location GLN.
 * @property {string} DIGITAL_LOCATION - A digital location GLN.
 * @property {string} FUNCTION - A function GLN.
 */
export type GLNType = 'LEGAL_ENTITY' | 'FIXED_PHYSICAL_LOCATION' | 'MOBILE_PHYSICAL_LOCATION' | 'DIGITAL_LOCATION' | 'FUNCTION';
/**
 * The status of a GLN.
 * @enum {string}
 */
export type GLNStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

/**
 * An array of attributes that are used in the GLN object. Used to switch between GLN and GLNInterface
 * @returns {string[]} - an array of attributes that are used in the GLN object.
 */
const GLNAttributes = [
  'gln', 'submitting_MO', 'glnType', 'glnTypes', 'status', 'licenceKey', 'name', 'parentOrganisationGLN', 'address',
  'latitude', 'longitude', 'digitalAddress', 'roleType','isMain', 'mainGln', 'url', 'isActive',
  'id', 'lastModified', 'lastModifiedBy', 'created', 'createdBy', 'isPublished',
  'companyId','siren','companyName','mobileLocationValue', 'codeNaf','vatCode', 'language', 'organisationCode','codeNumber'
];


/**
 * A regular expression that matches a coordinate in the format:
 * "latitude, longitude"
 * @returns A regular expression that matches a coordinate in the format:
 * "latitude, longitude"
 */
export const coordonateRegex = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

/**
 * A data structure that represents a GLN address.
 * @property {string} [postalName] - The name of the company.
 * @property {string} [streetAddress] - The street address of the company.
 * @property {string} [streetAddress2] - The second street address of the company.
 * @property {string} [postalCode] - The postal code of the company.
 * @property {string} [addressLocality] - The city of the company.
 * @property {string} [countryCode] - The country code of the company.
 */
export interface GLNAddress {
  postalName?: string;
  streetAddress?: string;
  streetAddress2?: string;
  postalCode?: string;
  addressLocality?: string;
  countryCode?: `${string}${string}`;
}

/**
 * A type that represents the types of GLNs that can be found in a GLN.
 * @property {boolean} isLegalEntity - Whether or not the GLN is a legal entity.
 * @property {boolean} isFixedPhysicalLocation - Whether or not the GLN is a fixed physical location.
 * @property {boolean} isMobilePhysicalLocation - Whether or not the GLN is a mobile physical location.
 * @property {boolean} isDigitalLocation - Whether or not the GLN is a digital location.
 * @property {boolean} isFunction - Whether or not the GLN is a function.
 */
export interface GLNTypes {
  isLegalEntity: boolean,
  isFixedPhysicalLocation: boolean,
  isMobilePhysicalLocation: boolean,
  isDigitalLocation: boolean,
  isFunction: boolean,
}

/**
 * A contact object that can be found in a GLN.
 * @property {string} lastName - The last name of the contact.
 * @property {string} firstName - The first name of the contact.
 * @property {string} email - The email of the contact.
 * @property {string} phone - The phone number of the contact.
 */
export interface GLNContact {
  lastName: string,
  firstName: string,
  email: string,
  phone: string
}

/**
 * A GLN object that contains all the information about a GLN.
 * @property {string} gln - The GLN number.
 * @property {string} [submitting_MO] - The submitting MO.
 * @property {GLNTypes} [glnType] - The GLN type.
 * @property {GLNType[]} glnTypes - The GLN types.
 * @property {GLNStatus} status - The GLN status.
 * @property {string} [licenceKey] - The licence key, generally the GCP.
 * @property {string} [name] - The name of the GLN.
 *
 */
export interface GLNInterface {
  readonly gln: string;
  submitting_MO?: string;
  glnType?: GLNTypes;
  glnTypes: GLNType[];
  status: GLNStatus;
  licenceKey?: string;
  name?: string;
  parentOrganisationGLN?: string;
  address?: GLNAddress;
  roleType?: string;
  latitude?: string;
  longitude?: string;
  mobileLocationValue?: string;
  digitalAddress?: string;
  contact?: GLNContact;
  language?: `${string}${string}`;
  url?: string;
  readonly lastModifiedBy?: string;
  readonly lastModified?: string;
  readonlyid?: string;
  created?: string;
  createdBy?: string;
  codeNaf?: string;
  siren?: string;
  vatCode?: string;
  companyId?: string;
  companyName?: string;
  isMain?: boolean;
  isActive?: boolean;
  mainGln?: string;
  isPublished?: boolean;
  [key: string]: any;
  organisationCode?: string;
  codeNumber?: string;
}

export interface CompanySearchResponse{
  readonly companyList?: GlnCompanySearchInfo[];
}
export class GlnCompanySearchInfo{
  readonly companyId?: string;
  readonly name?: string;
}
export class GLN implements GLNInterface {
  readonly lastModifiedBy?: string;
  readonly lastModified?: string;
  readonly id?: string;
  created?: string;
  createdBy?: string;
  readonly gln!: string;
  submitting_MO?: string;
  status!: GLNStatus;
  glnType?: GLNTypes;
  glnTypes: GLNType[] = [];
  licenceKey?: string;
  address?: GLNAddress | undefined;
  name?: string;
  roleType?: string;
  parentOrganisationGLN?: string;
  postalName?: string;
  streetAddress?: string;
  url?: string;
  language?: `${string}${string}`;
  latitude?: string;
  longitude?: string;
  digitalAddress?: string;
  contact?: GLNContact;
  codeNaf?: string;
  siren?: string;
  vatCode?: string;
  companyId?: string;
  companyName?: string;
  isMain?: boolean;
  isActive?: boolean;
  mainGln?: string;
  isPublished?: boolean;
  [key: string]: any;
  organisationCode?: string;
  codeNumber?: string;
  constructor(glnLike: GLNInterface) {
    for (let key of GLNAttributes) {
      if (glnLike[key]) {
        if (key === 'address') {
          this[key] = {...glnLike[key]}
        } else if(key === 'codeNumber' ){
          const organisationCode = glnLike.organisationCode || '';
          this[key] = organisationCode.length === 0 ? undefined : glnLike[key]
        }
        else {
          this[key] = glnLike[key];
        }
      }
    }
    if (this.areGLNTypesValid()) {
      this.glnTypes = getStringTypesFromBooleans(this.glnType);
    } else {
      this.glnType = getBooleansTypesFromStrings(this.glnTypes);
    }
    this.parentOrganisationGLN = glnLike.parentOrganisationGLN?.toString();
    this.roleType = glnLike.roleType;
    this.contact = glnLike.contact;
  }

  /**
   * Returns the label for the given GLN type.
   * @param {GLNType} type - the GLN type to get the label for.
   * @returns {string} the label for the given GLN type.
   */
  static getLabelForType(type: GLNType): string {
    switch (type) {
      case 'DIGITAL_LOCATION': return 'Digital'
      case 'FIXED_PHYSICAL_LOCATION': return 'Physique'
      case 'FUNCTION': return 'Fonction'
      case 'LEGAL_ENTITY': return 'Juridique'
      case 'MOBILE_PHYSICAL_LOCATION': return 'Mobile'
      default: return 'Type'
    }
  }

  /**
   * Validates the fields of the GLN.
   * @returns {boolean} - True if the fields are valid, false otherwise.
   */
  validate(): boolean {
    const basicFieldsValid = this.isNameValid() && this.areGLNTypesValid() && (!this.parentOrganisationGLN || this.isGLNNumberValid(this.parentOrganisationGLN));
    const addressValid = this.isAddressValid();
    if (this.glnTypes.includes('FIXED_PHYSICAL_LOCATION')) {
      return basicFieldsValid && addressValid === true;
    }
    return basicFieldsValid && addressValid !== false;
  }

  /**
   * Checks if the given string is a valid GLN number.
   * @param {string | number} gln - the string or number to check
   * @returns {boolean} - true if the string is a valid GLN number, false otherwise
   */
  isGLNNumberValid(gln: string | number = this.gln): boolean {
    const lookLikeGLN = gln.toString().length === 13 && /\d{13}/.test(gln.toString());
    return lookLikeGLN;
  }

  /**
   * Checks if the address is valid.
   * @returns {boolean | null} - true if the address is valid, false if it is not, null if the address is incomplete.
   */
  isAddressValid(): boolean | null {
    if (!this.address) {return false}
    if (!this.address.postalCode && !this.address.countryCode && !this.address.addressLocality && !this.address.streetAddress && !this.address.streetAddress2) {return null}
    if (!this.address.postalCode || !this.address.countryCode || !this.address.addressLocality || !this.address.streetAddress) {return false}
    if (this.address.postalCode.length === 0 || this.address.countryCode.length !== 2 || this.address.addressLocality.length === 0 || this.address.streetAddress.length === 0) {return false}
    return true
  }

  /**
   * Checks if the latitude and longitude are valid.
   * @returns {boolean} - True if the latitude and longitude are valid.
   */
  areCoordonatesValid(): boolean {
    return !!this.Latitude && coordonateRegex.test(this.Latitude)
            && !!this.Longitude && coordonateRegex.test(this.Longitude);
  }

  /**
   * Checks if the licence key is valid.
   * @param {string | undefined} licenceKey - the licence key to check.
   * @returns {boolean} - true if the licence key is valid, false otherwise.
   */
  isLicenseKeyValid(licenceKey: string | undefined = this.licenceKey): boolean {
    if (licenceKey) {
      const lookLikeLicenceKey = licenceKey.length >= 6 && licenceKey.length <= 12 && licenceKey.match(/\d/)?.length === licenceKey.length;
      return lookLikeLicenceKey;
    } else {
      return false
    }
  }

  /**
   * Checks if the name is valid.
   * @param {string | undefined} name - the name to check.
   * @returns {boolean} - true if the name is valid, false otherwise.
   */
  isNameValid(name: string | undefined = this.name): boolean {
    if (!name) {return true}
    name = name.trim();
    const lookLikeName = name !== undefined && name.length >=3 && name.length <= 100;
    return lookLikeName;
  }

  /**
   * Checks if the user has selected at least one GLN type.
   * @returns {boolean} - True if at least one GLN type is selected.
   */
  areGLNTypesValid(): boolean {
    const oneIsSelected = !!this.glnTypes && !!(this.glnType?.isDigitalLocation || this.glnType?.isFixedPhysicalLocation || this.glnType?.isFunction || this.glnType?.isLegalEntity || this.glnType?.isMobilePhysicalLocation);
    if(oneIsSelected && !!this.glnType?.isMobilePhysicalLocation)
    return !!this.glnType?.isMobilePhysicalLocation
    if (oneIsSelected) {
      return !(this.glnType?.isFixedPhysicalLocation && this.glnType.isMobilePhysicalLocation)
    } else {
      return oneIsSelected
    }
  }

  /**
   * Remove all unusual attributes of this object
   * @returns the cleaned GLN
   */
  getCleanedGLN(): GLNInterface {
    const gln: any = {};
    for (const key of GLNAttributes) {
      gln[key] = this[key];
    }
    return gln as GLNInterface;
  }

  /**
   * Adds a GLN type to the GLN.
   * @param {GLNType} type - the type to add to the GLN
   * @returns None
   */
  addGLN_type (type: GLNType) {
    if (this.glnTypes.includes(type)) {
      return;
    }
    if (type === "FIXED_PHYSICAL_LOCATION" && this.glnTypes.includes('MOBILE_PHYSICAL_LOCATION')
      || type === "MOBILE_PHYSICAL_LOCATION" && this.glnTypes.includes('FIXED_PHYSICAL_LOCATION')) {
        throw new Error(`Can't add the type ${type} to this GLN because of its other types`);
    }
    this.glnTypes.push(type);
  }

  /**
   * Removes the given GLN type from the list of this GLN types.
   * @param {GLNType} type - the GLN type to remove from the list of GLN types.
   * @returns None
   */
  removeGLN_type(type: GLNType) {
    if (this.glnTypes.includes(type)) {
      this.glnTypes = this.glnTypes.filter((glnType: GLNType) => glnType !== type)
    }
  }

}

/**
 * Takes in a GLNType object and returns an array of strings that represent the
 * types of the GLN.
 * @param {GLNTypes | undefined} glnType - the GLNType object to convert to an array of strings.
 * @returns {GLNType[]} - an array of strings that represent the types of the GLN.
 */
export const getStringTypesFromBooleans = (glnType: GLNTypes| undefined): GLNType[] => {
  const types: GLNType[] = [];
  if (!glnType) {
    return types
  }
  if (glnType.isMobilePhysicalLocation) {
    types.push('MOBILE_PHYSICAL_LOCATION');
  }
  if (glnType.isLegalEntity) {
    types.push('LEGAL_ENTITY');
  }
  if (glnType.isFunction) {
    types.push('FUNCTION');
  }
  if (glnType.isFixedPhysicalLocation) {
    types.push('FIXED_PHYSICAL_LOCATION');
  }

  if (glnType.isDigitalLocation) {
    types.push('DIGITAL_LOCATION');
  }
  return types;
}

/**
 * Takes in an array of GLN type and returns a GLN types object.
 * @param {GLNType[]} glnTypes - the array of GLN types
 * @returns {GLNTypes} - the GLN types object
 */
export const getBooleansTypesFromStrings = (glnTypes: GLNType[]): GLNTypes => {
  const types: GLNTypes = {
    isLegalEntity: false,
    isFixedPhysicalLocation: false,
    isMobilePhysicalLocation: false,
    isDigitalLocation: false,
    isFunction: false
  }
  if (glnTypes.length !== 0) {
    if (glnTypes.includes('DIGITAL_LOCATION')) {
      types.isDigitalLocation = true
    }
    if (glnTypes.includes('FIXED_PHYSICAL_LOCATION')) {
      types.isFixedPhysicalLocation = true
    }
    if (glnTypes.includes('FUNCTION')) {
      types.isFunction = true
    }
    if (glnTypes.includes('LEGAL_ENTITY')) {
      types.isLegalEntity = true
    }
    if (glnTypes.includes('MOBILE_PHYSICAL_LOCATION')) {
      types.isMobilePhysicalLocation = true
    }
  }
  return types;
}

/**
 * A validator that checks if the address is valid.
 * @param {AbstractControl} control - the control to validate
 * @returns {null | {'invalidAddress': true}} - null if the address is valid, otherwise an object containing the error.
 */
export const addressValidator = (control: AbstractControl) => {
    // If one of the field is filled, then all must be
    if (control.get('addressLocality')?.value || control.get('countryCode')?.value || control.get('streetAddress')?.value || control.get('postalCode')?.value || control.get('streetAddress2')?.value) {
      const city = control.get('addressLocality');
      const country = control.get('countryCode');
      const street = control.get('streetAddress');
      const postalCode = control.get('postalCode');
      // Check if any of the field is empty or invalid
      if (city?.invalid || country?.invalid || street?.invalid || postalCode?.invalid || !city?.value || !country?.value || !street?.value || !postalCode?.value) {
        return {'invalidAddressField': true}
      }
      if (!city?.value || !country?.value || !street?.value || !postalCode?.value) {
        return {'emptyAdressField': true}
      }
    }
    return null
}

